import { KEYS } from 'game/constants/SceneConstants';
import { SceneBase } from '@jollywise/jollygoodgame';
import { SCENARIOS } from '../constants/Constants';
import { StateScenarioIntro } from './states/StateScenarioIntro';
import { StateScenarioEmotionCheck } from './states/StateScenarioEmotionCheck';
import { StateScenarioEmotionNotice } from './states/StateScenarioEmotionNotice';
import { StateScenarioOutro } from './states/StateScenarioOutro';
import { StateScenarioInteraction } from './states/StateScenarioInteraction';
import { StateScenarioEmotionFeel } from './states/StateScenarioEmotionFeel';
import ScenarioCharacter from '../display/ScenarioCharacter';
import { AssetUnloader } from '@jollywise/jollygoodgame/src/utils/AssetUnloader';

export default class Scenario extends SceneBase {
  constructor() {
    super({ key: KEYS.Scenario, active: false });
  }

  init(properties) {
    this._scenario = SCENARIOS.find((s) => s.id === properties.data.scenarioId);
  }

  create() {
    super.create();
    this.playScenarioMusic();
    this.setBackgroundColour(0x505050);

    const { pack, keys } = this._scenario.assetpack;
    if (pack && keys) {
      this.game.loader.loadAssetPack(pack, keys, this.start.bind(this));
    } else {
      this.start();
    }

    this.game.addScenarioShortcuts();
  }

  start() {
    this._sceneContainer = this.add.layer();
    this._sceneObjects = this.renderScene(this._scenario.scene_objects);

    // set up states
    this._stateIntro = new StateScenarioIntro(this, this._scenario);
    this._stateEmotionCheck = new StateScenarioEmotionCheck(this, this._scenario);
    this._stateEmotionNotice = new StateScenarioEmotionNotice(this, this._scenario);
    this._stateEmotionFeel = new StateScenarioEmotionFeel(this, this._scenario);
    this._stateInteraction = new StateScenarioInteraction(this, this._scenario);
    this._stateOutro = new StateScenarioOutro(this, this._scenario);

    // attach event listeners
    this._stateIntro.on('state_complete', this.nextState, this);
    this._stateEmotionCheck.on('state_complete', this.nextState, this);
    this._stateEmotionNotice.on('state_complete', this.nextState, this);
    this._stateEmotionFeel.on('state_complete', this.nextState, this);
    this._stateInteraction.on('state_complete', this.nextState, this);
    this._stateOutro.on('state_complete', this.nextState, this);

    console.log('Scenario.start', this._scenario);
    // quick skip
    if (JW_DEBUG) this.input.keyboard.on('keydown-SPACE', this.nextState, this);

    // set flow
    this._stateOrder = [
      this._stateIntro,
      this._stateEmotionCheck,
      this._stateEmotionNotice,
      this._stateEmotionFeel,
      this._stateInteraction,
      this._stateOutro,
    ];
    this._stateIndex = -1;

    this.startStateByIndex(0);

    const { names = {} } = this._scenario.tracking;
    const roundNumber = this.game.player.getSessionScenarioPlayCount(this._scenario.id);
    this.game.stats.setRoundName(names.ROUND_NAME);
    this.game.stats.setScreenName(names.INTRO);
    this.game.stats.startRound(roundNumber);
  }

  playScenarioMusic() {
    if (this._scenario.background_music) {
      this.game.soundController.playMusic(this._scenario.background_music);
    } else this.game.soundController.stopMusic();
  }

  // =============================================================================================
  // SCENE METHODS
  // =============================================================================================

  nextState() {
    this.game.vo.stop();
    if (this._stateIndex == this._stateOrder.length - 1) this.endScenario();
    else this.startStateByIndex(this._stateIndex + 1);
  }

  previousState() {
    this.game.vo.stop();
    if (this._stateIndex > 0) this.startStateByIndex(this._stateIndex - 1);
    else this.game.showWelcome();
  }

  startStateByIndex(index) {
    this.setState(this._stateOrder[Phaser.Math.Clamp(index, 0, this._stateOrder.length - 1)]);
  }

  setState(state) {
    this._stateIndex = this._stateOrder.indexOf(state);
    this.stateMachine.setState(state);
  }

  setCharacterVisibility(characterid, visible) {
    this.setSceneObjectVisiblity(characterid, 'character', visible);
  }

  setSceneObjectVisiblity(id, type, visible) {
    const sceneObject = this.getSceneObject(id, type);
    if (sceneObject) sceneObject.visible = visible;
  }

  getSceneObject(id, type) {
    if (type) {
      return this._sceneObjects.find((o) => o.data?.values.type == type && o.data?.values.id == id);
    } else return this._sceneObjects.find((o) => o.data?.values.id == id);
  }

  endScenario() {
    const img = this.add.image(910, 390, 'background_pattern');
    this.tweens.add({
      targets: img,
      alpha: { from: 0, to: 1 },
      duration: 1000,
      onComplete: () => this.game.completeScenario(this._scenario.id),
    });
  }

  // =============================================================================================
  // SCENE CREATION
  // =============================================================================================

  renderScene(config) {
    const sceneObjects = [];
    config.forEach((entry, i) => {
      let sceneObject;
      switch (entry.type) {
        case 'rectangle':
          sceneObject = this.add.rectangle(entry.x, entry.y, entry.w, entry.h, entry.color);
          break;
        case 'image':
          sceneObject = this.add.image(entry.x, entry.y, entry.id);
          break;
        case 'character':
          sceneObject = new ScenarioCharacter(this, entry);
          break;
      }
      if (sceneObject) {
        sceneObject.setData('config', { ...entry });
        sceneObject.setData('type', entry.type);
        sceneObject.setData('id', entry.id);
        sceneObject.setData('layer', i);
        sceneObjects.push(sceneObject);
        // this._sceneContainer.add(sceneObject);
      }
    });
    return sceneObjects;
  }

  shutdown() {
    const { pack, keys } = this._scenario.assetpack;
    if (pack && keys) AssetUnloader.UnloadAssetpack(this, pack, keys);

    super.shutdown();
    this.game.vo.stop();
    this.game.clearScenarioShortcuts();
  }
}
